import { cn } from "@/lib/utils";
import type { InboxItem } from "@cohesive/api-client";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, MessageSquare, Plus } from "lucide-react";
import { useMemo } from "react";
import { Spinner } from "../../Spinner/Spinner";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
  gridCardPreviewPlaceholderIconClassName,
} from "../constants";
import {
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceCardStarButton } from "../WorkspaceCardStarButton";
import { WorkspaceGridCard } from "../WorkspaceGridCard";

export interface ChatCardProps {
  item: InboxItem;
  /** Selection/activation click handler (cmd/shift multi-select or open). */
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  /**
   * Right-click handler forwarded to the card root — wire to the shared
   * `useRailRowContextMenu` controller so right-click opens the same menu the
   * kebab does, at the cursor.
   */
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Registry-backed actions menu rendered in the footer (kebab + right-click).
   * Injected by the app; omit to render a card with no menu.
   */
  menuSlot?: React.ReactNode;
  /** Toggle for the on-card corner star. Omit to hide the star. */
  onToggleStar?: (item: InboxItem) => void;
  isSelected?: boolean;
  className?: string;
}

function cleanMessageContent(raw: string): string {
  return raw
    .replace(/<entity-ref[^>]*>(.*?)<\/entity-ref>/gs, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`{1,3}[\s\S]*?`{1,3}/g, "…")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\n+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function ChatCard({
  item,
  onClick,
  onDoubleClick,
  onContextMenu,
  menuSlot,
  onToggleStar,
  isSelected = false,
  className,
}: ChatCardProps) {
  const name = item.flow_session?.name || "Untitled chat";
  const relativeTime = item.updated_at
    ? formatDistanceToNow(new Date(item.updated_at), { addSuffix: true })
    : null;
  const isUnread = !item.is_read;
  const isStarred = item.is_starred;
  const hasToolConfirmations = item.has_pending_tool_confirmations;

  const lastMessage = item.flow_message;
  const isFromUser = lastMessage?.origin === "user";

  const previewText = useMemo(() => {
    if (!lastMessage?.content) return null;
    const cleaned = cleanMessageContent(lastMessage.content);
    if (!cleaned) return null;
    const MAX_CHARS = 120;
    return cleaned.length > MAX_CHARS
      ? cleaned.slice(0, MAX_CHARS).trimEnd() + "…"
      : cleaned;
  }, [lastMessage?.content]);

  const preview = (
    <>
      {previewText ? (
        <div
          className={cn(
            "absolute inset-0 flex items-center p-3",
            isFromUser ? "justify-end" : "justify-start",
          )}
        >
          <div
            className={cn(
              "max-w-[82%] rounded-2xl px-2.5 py-1.5 shadow-sm",
              isFromUser
                ? "rounded-tr-[4px] bg-foreground/[0.09] text-foreground/80 dark:bg-n-95/[0.10]"
                : "rounded-tl-[4px] border border-border/20 bg-background/85 text-foreground/75 backdrop-blur-sm dark:bg-n-95/[0.07]",
            )}
          >
            <div
              className="break-words text-[10.5px]"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: "15px",
                maxHeight: "45px",
              }}
            >
              {previewText}
            </div>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
          <MessageSquare
            className={gridCardPreviewPlaceholderIconClassName}
            strokeWidth={1.75}
          />
        </div>
      )}

      <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
        {hasToolConfirmations && (
          <span className="flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm">
            <AlertCircle className="h-3 w-3" />
            Action
          </span>
        )}
        {isUnread && (
          <span className="h-2 w-2 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
        )}
      </div>

      {onToggleStar && (
        <WorkspaceCardStarButton
          isFavorite={isStarred}
          onToggle={() => onToggleStar(item)}
        />
      )}
    </>
  );

  const footer = (
    <>
      <div className="min-w-0 flex-1">
        <WorkspaceCardFooterTitleRow
          icon={<WorkspaceCardFooterTypeIcon icon={MessageSquare} />}
        >
          <p className={gridCardFooterTitleClassName}>{name}</p>
        </WorkspaceCardFooterTitleRow>
        {relativeTime && (
          <p
            className={cn(
              gridCardFooterMetaRowClassName,
              "truncate",
              gridCardFooterSubtitleClassName,
            )}
          >
            {relativeTime}
          </p>
        )}
      </div>

      {menuSlot && <div className="shrink-0">{menuSlot}</div>}
    </>
  );

  return (
    <WorkspaceGridCard
      aria-label={name}
      aria-selected={isSelected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      preview={preview}
      footer={footer}
      isSelected={isSelected}
      interactive={onClick != null || onDoubleClick != null}
      className={className}
    />
  );
}

export interface NewChatCardProps {
  onCreate: () => void;
  isLoading?: boolean;
}

export function NewChatCard({ onCreate, isLoading = false }: NewChatCardProps) {
  const preview = (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
      {isLoading ? (
        <Spinner size="lg" className="text-muted-foreground" />
      ) : (
        <Plus
          className={gridCardPreviewPlaceholderIconClassName}
          strokeWidth={1.75}
        />
      )}
    </div>
  );

  const footer = (
    <div className="min-w-0 flex-1">
      <WorkspaceCardFooterTitleRow
        icon={<WorkspaceCardFooterTypeIcon icon={MessageSquare} />}
      >
        <p className={gridCardFooterTitleClassName}>New chat</p>
      </WorkspaceCardFooterTitleRow>
      <p
        className={cn(
          gridCardFooterMetaRowClassName,
          gridCardFooterSubtitleClassName,
        )}
      >
        Start a conversation
      </p>
    </div>
  );

  return (
    <WorkspaceGridCard
      aria-label="New chat"
      onClick={isLoading ? undefined : onCreate}
      preview={preview}
      footer={footer}
      dashed
      interactive={!isLoading}
      className={isLoading ? "cursor-default opacity-70" : undefined}
    />
  );
}
